import React from "react";
import PropTypes from "prop-types";

import Controls from "./controls";
import styles from "./style.module.scss";
import imgCircle from "./img/circle.svg";

/**
 * BlottingPaper Component.
 * This component renders the blotting paper section with its items.
 * It can be used in different sections like PREDICTIONS or RESULTS,
 * and provides visual feedback for items based on their volume.
 *
 * @prop {string} section - Represents the current section. E.g., PREDICTIONS, RESULTS, etc.
 * @prop {string} heading - Represents the heading/title for the section.
 * @prop {string} [description] - Represents a description or additional info for the section.
 * @prop {bool} [showCorrection] - A flag to indicate if correction messages should be shown.
 * @prop {Array} [data] - Represents data for individual items in the section.
 *
 */
export class BlottingPaper extends React.Component {
  render() {
    return (
      <div
        className={styles.section}
        label={"BlottingPaper-" + this.props.section}
      >
        <h3>{this.props.heading}</h3>

        {this.props.description && <p>{this.props.description}</p>}

        <ul className={styles.ul}>
          {this.props.data.map((item, index) => (
            <li
              className={styles.li}
              style={{
                width:
                  this.props.section === "PREDICTIONS"
                    ? "calc(20% - 32px)"
                    : "calc(25% - 32px)",
              }}
              key={item.label}
            >
              {/* Conditionally show volume for predictions */}
              {this.props.section === "PREDICTIONS" && (
                <p
                  className={styles.zoneVolume}
                  aria-label="Blotting paper sample, demonstrating 10μl size."
                >
                  {item.desired_volume + "μl"}
                </p>
              )}

              <div className={styles.indicator}>
                {/* Show controls only for predictions and if controls are enabled */}
                {this.props.section === "PREDICTIONS" &&
                  item.controls === true && <Controls index={index} />}

                <img
                  src={imgCircle}
                  className={styles.img}
                  alt={
                    "Blotting Paper Position " +
                    item.label +
                    ", covering " +
                    (item.volume / 50) * 100 +
                    "% surface area"
                  }
                  style={{
                    pointerEvents: "none",
                    width: (item.volume / 50) * 100 + "%",
                  }}
                />
              </div>

              {/* Show label and conditionally show volume for results */}
              <p className={styles.zoneLabel}>
                {item.label}
                {this.props.section === "RESULTS" && " | " + item.volume + "μl"}
              </p>

              {/* Show correction if flag is enabled */}
              <div className={styles.correction}>
                {this.props.showCorrection && (
                  <p className={styles.incorrect}>Incorrect!</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

// Prop types for the BlottingPaper component
BlottingPaper.propTypes = {
  section: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  description: PropTypes.string,
  showCorrection: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      desired_volume: PropTypes.number,
      controls: PropTypes.bool,
      volume: PropTypes.number,
    })
  ).isRequired,
};

// Default props for the BlottingPaper component
BlottingPaper.defaultProps = {
  showCorrection: false,
};
